import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  Save,
  Plus,
  X,
  Calendar,
  Clock,
  Users,
  Vote,
  AlertCircle,
  Info,
  XCircle
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const VotacaoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCondominioId, isAdministrador } = useCondominio();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const podeCriarEditar = ['administrador', 'sindico', 'subsindico'].includes(user?.usuario_tipo);

  const [formData, setFormData] = useState({
    votacao_titulo: '',
    votacao_descricao: '',
    votacao_data_inicio: '',
    votacao_duracao_dias: '',
    votacao_duracao_horas: '',
    votacao_tipo_validacao: 'em_dia',
    votacao_status: 'pendente',
    assembleia_id: '',
    itens: [{ texto: '', ordem: 0 }, { texto: '', ordem: 1 }]
  });

  const [errors, setErrors] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);

  // Buscar assembleias
  const { data: assembleiasData } = useQuery(
    ['assembleias', getCondominioId()],
    () => api.get('/assembleias', {
      params: { condominio_id: getCondominioId() }
    }).then(res => res.data.data.assembleias || []),
    {
      enabled: !!getCondominioId()
    }
  );

  // Buscar votação para edição
  const { data: votacaoData, isLoading: isLoadingVotacao } = useQuery(
    ['votacao', id],
    () => api.get(`/votacoes/${id}`, {
      params: { condominio_id: getCondominioId() }
    }).then(res => res.data.data.votacao),
    {
      enabled: isEdit && !!id && !!getCondominioId(),
      onSuccess: (data) => {
        if (data) {
          setFormData({
            votacao_titulo: data.votacao_titulo || '',
            votacao_descricao: data.votacao_descricao || '',
            votacao_data_inicio: data.votacao_data_inicio ? 
              new Date(data.votacao_data_inicio).toISOString().slice(0, 16) : '',
            votacao_duracao_dias: data.votacao_duracao_dias || '',
            votacao_duracao_horas: data.votacao_duracao_horas || '',
            votacao_tipo_validacao: data.votacao_tipo_validacao || 'em_dia',
            votacao_status: data.votacao_status || 'pendente',
            assembleia_id: data.assembleia_id || '',
            itens: data.itens && data.itens.length > 0 
              ? data.itens.map((item, index) => ({ texto: item.votacao_item_texto, ordem: index }))
              : [{ texto: '', ordem: 0 }, { texto: '', ordem: 1 }]
          });
        }
      }
    }
  );

  // Criar/atualizar votação
  const votacaoMutation = useMutation(
    (data) => {
      const url = isEdit 
        ? `/votacoes/${id}`
        : '/votacoes';
      const method = isEdit ? 'put' : 'post';
      
      return api[method](url, {
        ...data,
        condominio_id: getCondominioId()
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['votacoes']);
        toast.success(isEdit ? 'Votação atualizada com sucesso!' : 'Votação criada com sucesso!');
        navigate('/votacoes');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao salvar votação');
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      }
    }
  );

  useEffect(() => {
    if (!podeCriarEditar) {
      toast.error('Você não tem permissão para criar ou editar votações');
      navigate('/votacoes');
    }
  }, [podeCriarEditar, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.votacao_titulo.trim()) {
      newErrors.votacao_titulo = 'Título é obrigatório';
    }

    if (formData.itens.length < 2) {
      newErrors.itens = 'É necessário pelo menos 2 opções de voto';
    }

    formData.itens.forEach((item, index) => {
      if (!item.texto.trim()) {
        newErrors[`item_${index}`] = 'Texto da opção é obrigatório';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    const dataToSend = {
      ...formData,
      itens: formData.itens.map((item, index) => ({
        texto: item.texto,
        ordem: item.ordem !== undefined ? item.ordem : index
      }))
    };

    votacaoMutation.mutate(dataToSend);
  };

  const adicionarItem = () => {
    setFormData({
      ...formData,
      itens: [...formData.itens, { texto: '', ordem: formData.itens.length }]
    });
  };

  const removerItem = (index) => {
    if (formData.itens.length <= 2) {
      toast.error('É necessário pelo menos 2 opções');
      return;
    }
    setFormData({
      ...formData,
      itens: formData.itens.filter((_, i) => i !== index).map((item, i) => ({ ...item, ordem: i }))
    });
  };

  const atualizarItem = (index, texto) => {
    const novosItens = [...formData.itens];
    novosItens[index].texto = texto;
    setFormData({ ...formData, itens: novosItens });
    
    // Limpar erro do item
    if (errors[`item_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`item_${index}`];
      setErrors(newErrors);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Limpar erro do campo
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  if (isLoadingVotacao && isEdit) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Cabeçalho */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/votacoes')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} />
          Voltar para Votações
        </button>
        
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Vote className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              {isEdit ? 'Editar Votação' : 'Nova Votação'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEdit ? 'Atualize as informações da votação' : 'Crie uma nova votação para o condomínio'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6 space-y-6">
        {/* Informações Básicas */}
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Info size={20} />
            Informações Básicas
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título da Votação *
              </label>
              <input
                type="text"
                value={formData.votacao_titulo}
                onChange={(e) => handleInputChange('votacao_titulo', e.target.value)}
                placeholder="Ex: Aprovação de reforma do salão de festas"
                required
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.votacao_titulo ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.votacao_titulo && (
                <p className="mt-1 text-sm text-red-600">{errors.votacao_titulo}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={formData.votacao_descricao}
                onChange={(e) => handleInputChange('votacao_descricao', e.target.value)}
                rows={4}
                placeholder="Descreva os detalhes da votação, contexto, importância, etc."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                Forneça informações detalhadas para que os moradores possam tomar uma decisão informada.
              </p>
            </div>
          </div>
        </div>

        {/* Configurações de Tempo */}
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Clock size={20} />
            Período da Votação
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data e Hora de Início
              </label>
              <input
                type="datetime-local"
                value={formData.votacao_data_inicio}
                onChange={(e) => handleInputChange('votacao_data_inicio', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                Se não preencher, a votação iniciará imediatamente ao ser ativada.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status Inicial
              </label>
              <select
                value={formData.votacao_status}
                onChange={(e) => handleInputChange('votacao_status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="pendente">Pendente</option>
                <option value="ativa">Ativa</option>
                <option value="encerrada">Encerrada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duração (dias)
              </label>
              <input
                type="number"
                min="0"
                value={formData.votacao_duracao_dias}
                onChange={(e) => handleInputChange('votacao_duracao_dias', e.target.value)}
                placeholder="Ex: 7"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                Quantos dias a votação ficará aberta
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duração (horas)
              </label>
              <input
                type="number"
                min="0"
                max="23"
                value={formData.votacao_duracao_horas}
                onChange={(e) => handleInputChange('votacao_duracao_horas', e.target.value)}
                placeholder="Ex: 12"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                Horas adicionais (0-23)
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <Info size={16} className="inline mr-2" />
              A data de término será calculada automaticamente com base na data de início e duração informadas.
            </p>
          </div>
        </div>

        {/* Validação e Elegibilidade */}
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Users size={20} />
            Elegibilidade para Votar
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Validação *
              </label>
              <select
                value={formData.votacao_tipo_validacao}
                onChange={(e) => handleInputChange('votacao_tipo_validacao', e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="em_dia">Em dia com boletos</option>
                <option value="procuração">Com procuração</option>
                <option value="liberacao">Com liberação (síndico/financeiro)</option>
                <option value="todos">Todos os usuários</option>
              </select>
              <div className="mt-2 space-y-1 text-sm text-gray-600">
                <p><strong>Em dia com boletos:</strong> Apenas moradores sem boletos em atraso podem votar.</p>
                <p><strong>Com procuração:</strong> Requer procuração registrada no sistema.</p>
                <p><strong>Com liberação:</strong> Requer liberação do síndico ou financeiro, ou estar em dia.</p>
                <p><strong>Todos os usuários:</strong> Qualquer usuário ativo pode votar.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assembleia (opcional)
              </label>
              <select
                value={formData.assembleia_id}
                onChange={(e) => handleInputChange('assembleia_id', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Nenhuma - Votação independente</option>
                {assembleiasData?.map(assembleia => (
                  <option key={assembleia.assembleia_id} value={assembleia.assembleia_id}>
                    {assembleia.assembleia_titulo} - {new Date(assembleia.assembleia_data).toLocaleDateString('pt-BR')}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Vincule esta votação a uma assembleia existente (opcional)
              </p>
            </div>
          </div>
        </div>

        {/* Opções de Voto */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Vote size={20} />
              Opções de Voto *
            </h2>
            <div className="flex gap-2">
              {/* Templates Rápidos */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                >
                  <Vote size={18} />
                  Templates
                </button>
                {showTemplates && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowTemplates(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-20 min-w-[250px]">
                      <div className="p-2">
                        <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Templates Rápidos</p>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              itens: [
                                { texto: 'Sim', ordem: 0 },
                                { texto: 'Não', ordem: 1 }
                              ]
                            });
                            setShowTemplates(false);
                            toast.success('Template "Sim / Não" aplicado!');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center gap-2"
                        >
                          <Vote size={16} className="text-purple-600" />
                          Sim / Não
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              itens: [
                                { texto: 'Aprovar', ordem: 0 },
                                { texto: 'Rejeitar', ordem: 1 },
                                { texto: 'Abstenção', ordem: 2 }
                              ]
                            });
                            setShowTemplates(false);
                            toast.success('Template "Aprovar / Rejeitar / Abstenção" aplicado!');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center gap-2"
                        >
                          <Vote size={16} className="text-purple-600" />
                          Aprovar / Rejeitar / Abstenção
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              itens: [
                                { texto: 'Concordo', ordem: 0 },
                                { texto: 'Discordo', ordem: 1 },
                                { texto: 'Abstenção', ordem: 2 }
                              ]
                            });
                            setShowTemplates(false);
                            toast.success('Template "Concordo / Discordo / Abstenção" aplicado!');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center gap-2"
                        >
                          <Vote size={16} className="text-purple-600" />
                          Concordo / Discordo / Abstenção
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              itens: [
                                { texto: 'Opção 1', ordem: 0 },
                                { texto: 'Opção 2', ordem: 1 },
                                { texto: 'Opção 3', ordem: 2 }
                              ]
                            });
                            setShowTemplates(false);
                            toast.success('Template "Opção 1 / 2 / 3" aplicado!');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center gap-2"
                        >
                          <Vote size={16} className="text-purple-600" />
                          Opção 1 / 2 / 3
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={adicionarItem}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
              >
                <Plus size={18} />
                Adicionar Opção
              </button>
            </div>
          </div>

          {errors.itens && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <AlertCircle size={16} />
                {errors.itens}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {formData.itens.map((item, index) => (
              <div key={index} className="flex gap-3 items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
                      {index + 1}
                    </span>
                    <label className="block text-sm font-medium text-gray-700">
                      Opção de Voto *
                    </label>
                  </div>
                  <input
                    type="text"
                    value={item.texto}
                    onChange={(e) => atualizarItem(index, e.target.value)}
                    placeholder={`Ex: Sim, Não, Aprovar, Rejeitar, Opção ${index + 1}, etc.`}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base ${
                      errors[`item_${index}`] ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                  />
                  {errors[`item_${index}`] && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors[`item_${index}`]}
                    </p>
                  )}
                </div>
                {formData.itens.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removerItem(index)}
                    className="mt-8 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                    title="Remover opção"
                  >
                    <XCircle size={20} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Preview das Opções */}
          {formData.itens.some(item => item.texto.trim()) && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Preview das Opções:</p>
              <div className="flex flex-wrap gap-2">
                {formData.itens.map((item, index) => (
                  item.texto.trim() && (
                    <span
                      key={index}
                      className="px-3 py-1 bg-white border border-blue-300 rounded-full text-sm text-blue-700 font-medium"
                    >
                      {item.texto}
                    </span>
                  )
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">
              <AlertCircle size={16} className="inline mr-2" />
              <strong>Importante:</strong> É necessário pelo menos 2 opções. Após a votação começar, não será possível alterar as opções.
            </p>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/votacoes')}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={votacaoMutation.isLoading}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {votacaoMutation.isLoading ? (
              <>
                <Clock size={18} className="animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={18} />
                {isEdit ? 'Atualizar Votação' : 'Criar Votação'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VotacaoForm;

